/**
 * Company writes shared by the Companies pages, case settings and the case
 * panel: call the endpoint, refresh every company view, and say what happened
 * with an Undo when there is one. Errors toast and resolve to null.
 */
import { toast } from "sonner";

import { BillingAPI, CompaniesAPI, PlaybookAPI } from "./api/companiesApi";
import { liveRules, liveWith, ruleLabel, toInput } from "./playbookModel";
import { refreshCompanies } from "./queries";
import type {
  BillingPatch,
  CaseLinkPatch,
  Company,
  CompanyBilling,
  CompanyPatch,
  LinkOptions,
  MatterIds,
  PlaybookRule,
  PlaybookRuleInput
} from "./types";

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const attempt = async <T>(run: () => Promise<T>): Promise<T | null> => {
  try {
    return await run();
  } catch (error) {
    toast.error(
      error instanceof Error ? error.message : "Couldn't update the company"
    );
    return null;
  } finally {
    void refreshCompanies();
  }
};

const undoable = (message: string, undo: () => Promise<unknown>) =>
  toast(message, {
    action: {
      label: "Undo",
      onClick: () => {
        void attempt(undo);
      }
    }
  });

const restorable = (company: Company, patch: CompanyPatch): CompanyPatch => {
  const out: Record<string, unknown> = {};
  (Object.keys(patch) as Array<keyof CompanyPatch>).forEach((key) => {
    out[key] = company[key as keyof Company];
  });
  return out as CompanyPatch;
};

const syncedNote = (synced: string[] | undefined) =>
  synced?.length
    ? ` and ${plural(synced.length, "linked case", "linked cases")}`
    : "";

export const companyActions = {
  create: (body: CompanyPatch & { individual_ids?: string[] }) =>
    attempt(async () => {
      const company = await CompaniesAPI.create(body);
      const linked = body.individual_ids?.length ?? 0;
      toast.success(
        linked
          ? `Created ${company.name} and linked ${plural(
              linked,
              "case",
              "cases"
            )}`
          : `Created ${company.name}`
      );
      return company;
    }),

  /** Save part of the record; Undo puts the previous values back. */
  save: (company: Company, patch: CompanyPatch, message?: string) =>
    attempt(async () => {
      const saved = await CompaniesAPI.update(company.id, patch);
      const before = restorable(company, patch);
      undoable(
        `${message ?? `Saved ${saved.company.name}`}${syncedNote(
          saved.synced_cases
        )}`,
        () => CompaniesAPI.update(company.id, before)
      );
      return saved.company;
    }),

  link: (company: Company, ids: string[], options: LinkOptions = {}) =>
    attempt(async () => {
      const linked = await CompaniesAPI.link(company.id, ids, options);
      undoable(
        `Linked ${plural(linked.length, "case", "cases")} to ${company.name}`,
        () => CompaniesAPI.unlink(company.id, linked)
      );
      return linked;
    }),

  unlink: (company: Company, ids: string[], restore: LinkOptions = {}) =>
    attempt(async () => {
      const unlinked = await CompaniesAPI.unlink(company.id, ids);
      undoable(
        `Unlinked ${plural(
          unlinked.length,
          "case",
          "cases"
        )}. Their employer details stay as they were.`,
        () => CompaniesAPI.link(company.id, unlinked, restore)
      );
      return unlinked;
    }),

  updateCase: (
    company: Company,
    individualId: string,
    patch: CaseLinkPatch,
    before: CaseLinkPatch
  ) =>
    attempt(async () => {
      const out = await CompaniesAPI.updateCase(
        company.id,
        individualId,
        patch
      );
      if (out.changed.length) {
        undoable("Saved", () =>
          CompaniesAPI.updateCase(company.id, individualId, before)
        );
      }
      return out;
    }),

  saveMatterId: (
    company: Company,
    individualId: string,
    field: keyof MatterIds,
    value: string,
    previous: string | null
  ) =>
    companyActions.updateCase(
      company,
      individualId,
      { [field]: value },
      { [field]: previous ?? "" }
    ),

  decideProposal: (
    company: Company,
    proposalId: string,
    decision: "accept" | "dismiss"
  ) =>
    attempt(async () => {
      const saved = await CompaniesAPI.decideProposal(
        company.id,
        proposalId,
        decision
      );
      toast.success(
        decision === "accept"
          ? `Saved the FEIN on ${saved.company.name}${syncedNote(
              saved.synced_cases
            )}`
          : "Dismissed"
      );
      return saved.company;
    })
};

/** Playbook edits (firm admins) and per-case overrides, each with an Undo. */
export const playbookActions = {
  add: (company: Company, rule: PlaybookRuleInput) =>
    attempt(async () => {
      const out = await PlaybookAPI.add(company.id, rule);
      undoable(
        out.rule.label ? `Added rule ${out.rule.label}` : "Added the rule",
        () =>
          PlaybookAPI.save(company.id, liveWith(out.rules, out.rule.id, null))
      );
      return out;
    }),

  update: (
    company: Company,
    rule: PlaybookRule,
    patch: PlaybookRuleInput,
    message: string
  ) =>
    attempt(async () => {
      const before = toInput(rule);
      const out = await PlaybookAPI.save(
        company.id,
        liveWith(company.playbook, rule.id, patch)
      );
      undoable(message, () =>
        PlaybookAPI.save(company.id, liveWith(out.rules, rule.id, before))
      );
      return out;
    }),

  remove: (company: Company, rule: PlaybookRule) =>
    attempt(async () => {
      const out = await PlaybookAPI.save(
        company.id,
        liveWith(company.playbook, rule.id, null)
      );
      undoable(`Deleted ${ruleLabel(rule) ?? "the rule"}`, () =>
        PlaybookAPI.save(company.id, [
          ...liveRules(out.rules).map(toInput),
          toInput(rule)
        ])
      );
      return out;
    }),

  decide: (
    company: Company,
    rule: PlaybookRule,
    decision: "accept" | "dismiss",
    edits: PlaybookRuleInput = {}
  ) =>
    attempt(async () => {
      const out = await PlaybookAPI.decide(
        company.id,
        rule.id,
        decision,
        edits
      );
      const added = out.rule;
      if (decision === "accept" && added) {
        undoable(
          added.label ? `Added rule ${added.label}` : "Added the rule",
          () =>
            PlaybookAPI.save(company.id, liveWith(out.rules, added.id, null))
        );
      } else {
        toast("Dismissed the suggestion");
      }
      return out;
    }),

  extract: (company: Company, guidelineIds: string[]) =>
    attempt(async () => {
      const out = await PlaybookAPI.extract(company.id, guidelineIds);
      const skipped = out.skipped
        ? ` ${plural(out.skipped, "was", "were")} already in the playbook.`
        : "";
      toast(
        out.suggested.length
          ? `Suggested ${plural(
              out.suggested.length,
              "rule",
              "rules"
            )} for review.${skipped}`
          : `No new instructions in those documents.${skipped}`
      );
      return out;
    }),

  setOverride: (
    company: Company,
    individualId: string,
    rule: PlaybookRule,
    on: boolean
  ) =>
    attempt(async () => {
      const out = await PlaybookAPI.setOverride(individualId, rule.id, on);
      undoable(
        on
          ? "Rule back on for this case"
          : `Rule off for this case only. ${company.name}'s other cases still follow it.`,
        () => PlaybookAPI.setOverride(individualId, rule.id, !on)
      );
      return out;
    })
};

const billingBefore = (
  billing: CompanyBilling,
  patch: BillingPatch
): BillingPatch => {
  const out: Record<string, unknown> = {};
  (Object.keys(patch) as Array<keyof BillingPatch>).forEach((key) => {
    out[key] = billing[key];
  });
  return out as BillingPatch;
};

/** Company billing terms (firm admins), each save with an Undo. */
export const billingActions = {
  save: (
    company: Company,
    billing: CompanyBilling,
    patch: BillingPatch,
    message = "Saved the billing terms"
  ) =>
    attempt(async () => {
      const out = await BillingAPI.save(company.id, patch);
      const before = billingBefore(billing, patch);
      const cases = out.changed_cases.length;
      undoable(
        cases
          ? `${message} and ${plural(
              cases,
              "case that follows",
              "cases that follow"
            )} the schedule`
          : message,
        () => BillingAPI.save(company.id, before)
      );
      return out.billing;
    }),

  markSubmitted: (company: Company, userId: string) =>
    attempt(async () => {
      const out = await BillingAPI.markSubmitted(company.id, userId);
      toast(
        `Recorded. Submit the rate in ${
          out.export.ebilling_system || `${company.name}'s e-billing system`
        }.`
      );
      return out;
    })
};
